import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  model,
  OnInit,
  output,
  signal
} from '@angular/core';
import {AbstractControl, FormGroup} from '@angular/forms';
import {
  DeclarationStatus,
  GetRotterdamDeclarationDetails,
  HarbourDuesDeclaration,
  HarbourDuesRotterdamResponse,
  HarbourDuesShippingCompany,
  Visit
} from '@portbase/bezoekschip-service-typescriptmodels';
import {HarbourDuesDeclarationTab} from '../../types';
import {HarbourDuesPermissionService} from "../../services/harbour-dues-permission.service";
import {sendQuery} from "../../../../common/utils";
import {VisitContext} from "../../../visit-context";
import {rxResource, takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'app-harbour-dues-rotterdam-details-tab',
  templateUrl: './harbour-dues-rotterdam-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HarbourDuesDetailsRotterdamComponent implements OnInit {
  private readonly permissionService = inject(HarbourDuesPermissionService);
  private readonly destroyRef = inject(DestroyRef);

  readonly form = input.required<FormGroup>();
  readonly showValidationErrors = input(false);
  readonly isAdmin = input(false);
  readonly isHarbourDuesAdvisor = input(false);
  readonly declarationTabs = input<HarbourDuesDeclarationTab[]>([]);
  readonly selectedDeclaration = input.required<HarbourDuesDeclaration>();
  readonly visit = input.required<Visit>();
  readonly declarationSelected = input(false);
  readonly hasBerthVisits = input(false);
  readonly vesselDeadWeightKg = input<number>();

  readonly proFormaRequested = output<void>();

  readonly validProFormaDisplayed = model<boolean>();

  readonly canEdit = computed(() =>
    this.permissionService.canEdit(this.selectedDeclaration())
  );

  // The selected shipping company's SAP HBR business partner id, or null when none is selected.
  // Drives (re-)fetching the declaration details so the liner services are re-enriched for it.
  private readonly selectedBusinessPartnerId = signal<string | null>(null);

  // Always runs on init (business partner id null -> backend enriches from the visit carrier) and
  // re-runs whenever the selected shipping company changes.
  private readonly detailsResource = rxResource<HarbourDuesRotterdamResponse, string | null>({
    params: () => this.selectedBusinessPartnerId(),
    stream: ({params}) => sendQuery(
      "com.portbase.bezoekschip.common.api.harbourdues.queries.GetRotterdamDeclarationDetails",
      <GetRotterdamDeclarationDetails>{
        crn: VisitContext.visit.crn,
        businessPartnerIdSapHbr: params
      })
  });

  readonly harbourDuesDetails = this.detailsResource.value;

  ngOnInit(): void {
    const shippingCompany = this.form().get('discount.rotterdam.shippingCompany') as AbstractControl | null;
    if (!shippingCompany) {
      return;
    }
    shippingCompany.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: HarbourDuesShippingCompany | null) =>
        this.selectedBusinessPartnerId.set(value?.businessPartnerIdSapHbr ?? null));
  }

  readonly isAccepted = computed(
    () => this.selectedDeclaration()?.status === DeclarationStatus.ACCEPTED
  );

  readonly isDeclared = computed(
    () => this.selectedDeclaration()?.status === DeclarationStatus.DECLARED
  );

  protected readonly hasCurrentVersionInvoice = computed(() => {
    const declaration = this.selectedDeclaration();
    return (
      declaration?.invoices?.some(
        i => i.statementVersion === declaration.statementVersion
      ) || false
    );
  });
}
